/**
 * Position Row Component
 *
 * Displays a single strategy position in the trader dashboard list.
 * Shows exchange logo, symbol, prices (spot/futures), liquidation, funding rate,
 * remaining balances, and P&L.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import { useTranslation } from 'react-i18next'; // Unused
import { Tooltip, Box, Typography, Divider, Avatar, Checkbox } from '@mui/material';
import { BalanceBreakdownTooltip } from './BalanceBreakdownTooltip';
import { FilledVolumeTooltip } from './FilledVolumeTooltip';
import LockIcon from '@mui/icons-material/Lock';
import { StrategyActionsMenu } from './StrategyActionsMenu';
import type { StrategyAction } from './StrategyActionsMenu';
import type { StrategyInfo } from '../../types/strategy';
import { getStateColor, getStateDisplayNameKo } from '../../types/strategy';
import { getExchangeLogo, EXCHANGE_NAMES } from '../../utils/exchangeUrls';
import { formatVolume as formatVolumeUtil } from '../../utils/formatters';
import { getUsdtKrwRate } from '../../utils/currencyConversion';
import { usePnlCalculation } from '../../hooks/usePnlCalculation';
import { usePriceUpdates } from '../../hooks/usePriceUpdates';
import { traderTheme as theme } from './traderDashboardTheme';

/**
 * Convert exchange name string to index for logo lookup
 */
function getExchangeIndex(exchangeName: string | undefined | null): number | undefined {
  if (!exchangeName) return undefined;
  const normalized = exchangeName.toLowerCase().replace('.', '').replace('-', '');
  const index = EXCHANGE_NAMES.findIndex(name => name === normalized);
  return index >= 0 ? index : undefined;
}

/**
 * Format volume for position display (handles null/undefined)
 */
function formatPositionVolume(volume: number | undefined | null): string {
  if (volume === undefined || volume === null) return '-';
  if (volume === 0) return '0';
  return formatVolumeUtil(Math.abs(volume), 6);
}

/**
 * Format profit with sign in USDT
 */
function formatProfit(profit: number | undefined | null): string {
  if (profit === undefined || profit === null) return '-';
  const sign = profit >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(profit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Format percentage with sign
 */
function formatPercent(value: number | undefined | null): string {
  if (value === undefined || value === null) return '-';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(4)}%`;
}

/**
 * Calculate which profit tiers have been filled and which remain
 * Returns { filled: ProfitTier[], remaining: ProfitTier[] }
 */
function calculateProfitTierSplit(
  profitTiers: Array<{ profit_percentage: number; volume_percentage_to_sell: number }> | null | undefined,
  totalVolume: number,
  filledVolume: number
): { filled: Array<{ profit_percentage: number; volume_percentage_to_sell: number }>; remaining: Array<{ profit_percentage: number; volume_percentage_to_sell: number }> } {
  if (!profitTiers || profitTiers.length === 0 || totalVolume === 0) {
    return { filled: [], remaining: profitTiers || [] };
  }

  const filledPercentage = (filledVolume / totalVolume) * 100;
  let accumulatedPercentage = 0;
  const filled: Array<{ profit_percentage: number; volume_percentage_to_sell: number }> = [];
  const remaining: Array<{ profit_percentage: number; volume_percentage_to_sell: number }> = [];

  for (const tier of profitTiers) {
    const tierVolumePercent = tier.volume_percentage_to_sell * 100;

    if (accumulatedPercentage + tierVolumePercent <= filledPercentage) {
      // Entire tier is filled
      filled.push(tier);
      accumulatedPercentage += tierVolumePercent;
    } else if (accumulatedPercentage < filledPercentage) {
      // Tier is partially filled - split it
      const filledPortion = filledPercentage - accumulatedPercentage;
      const remainingPortion = tierVolumePercent - filledPortion;

      filled.push({
        profit_percentage: tier.profit_percentage,
        volume_percentage_to_sell: filledPortion / 100,
      });

      remaining.push({
        profit_percentage: tier.profit_percentage,
        volume_percentage_to_sell: remainingPortion / 100,
      });

      accumulatedPercentage = filledPercentage;
    } else {
      // Tier is completely remaining
      remaining.push(tier);
    }
  }

  return { filled, remaining };
}

/**
 * Format USDT price with consistent decimal places
 */
function formatUsdt(price: number | undefined | null): string {
  if (price === undefined || price === null) return '-';
  return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
}

/**
 * Sub-cell style for multi-line cells
 */
const subTextStyle = {
  fontSize: { xs: '0.7rem', md: '0.8rem' },
  color: 'text.secondary',
  mt: 0.25,
};

export interface PositionRowProps {
  strategy: StrategyInfo;
  /** Callback when any action is triggered (emergency_clear, pause, resume, reload, update_budget) */
  onAction: (strategyId: string, action: StrategyAction) => void;
  /** Currently loading action for this strategy */
  loadingAction?: StrategyAction | null;
  /** Called when symbol is clicked - opens strategy dialog with given base asset */
  onSymbolClick?: (baseAsset: string) => void;
  /** Called when mouse enters the row - freezes sort order */
  onHoverStart?: (strategyId: string) => void;
  /** Called when mouse leaves the row - unfreezes sort order */
  onHoverEnd?: () => void;
  /** Whether this row is currently hovered (for visual indicator) */
  isHovered?: boolean;
  /** Whether multi-select mode is enabled */
  selectionMode?: boolean;
  /** Whether this row is selected */
  isSelected?: boolean;
  /** Callback when selection changes */
  onSelectionChange?: (strategyId: string, selected: boolean) => void;
}

/**
 * Format elapsed time from a start timestamp to now (updates every minute)
 */
function useElapsedTime(startTime: number | null | undefined): string {
  const [elapsed, setElapsed] = useState('');

  useEffect(() => {
    if (!startTime) { setElapsed(''); return; }

    const update = () => {
      const diff = Math.max(0, Date.now() - startTime);
      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);

      if (days > 0) {
        setElapsed(hours > 0 ? `${days}d ${hours}h` : `${days}d`);
      } else if (hours > 0) {
        setElapsed(minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`);
      } else {
        setElapsed(`${minutes}m`);
      }
    };

    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [startTime]);

  return elapsed;
}

/**
 * Calculate funding timer string from next_funding_time
 */
function useFundingTimer(nextFundingTime: number | null): string {
  const [timer, setTimer] = useState('00:00:00');

  useEffect(() => {
    const updateTimer = () => {
      const now = Date.now();

      let nextFunding: number;
      if (nextFundingTime && nextFundingTime > now) {
        nextFunding = nextFundingTime;
      } else {
        // Fallback: calculate next 8-hour interval (00:00, 08:00, 16:00 UTC)
        const nowDate = new Date();
        const hours = nowDate.getUTCHours();
        const nextFundingHour = Math.ceil(hours / 8) * 8;
        const nextFundingDate = new Date(nowDate);
        nextFundingDate.setUTCHours(nextFundingHour, 0, 0, 0);
        if (nextFundingDate.getTime() <= now) {
          nextFundingDate.setUTCHours(nextFundingHour + 8, 0, 0, 0);
        }
        nextFunding = nextFundingDate.getTime();
      }

      const diff = Math.max(0, nextFunding - now);
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((diff % (1000 * 60)) / 1000);

      setTimer(
        `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
      );
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [nextFundingTime]);

  return timer;
}

export const PositionRow: React.FC<PositionRowProps> = React.memo(({
  strategy,
  onAction,
  loadingAction,
  onSymbolClick,
  onHoverStart,
  onHoverEnd,
  isHovered,
  selectionMode = false,
  isSelected = false,
  onSelectionChange,
}) => {
  // const { t } = useTranslation(); // Unused
  const ctx = strategy.execution_context;
  const opportunity = ctx.opportunity;
  const position = ctx.position;

  // Use the P&L calculation hook
  const pnl = usePnlCalculation({
    ctx,
    realtimePrices: strategy.realtime_prices,
  });

  // Calculate profit tier split based on filled volume (memoized to prevent re-calculation on every render)
  const profitTierSplit = useMemo(() => {
    const totalVolume = (pnl.balance.spotAsked || 0) + (pnl.balance.spotRemaining || 0);
    const filledVolume = pnl.balance.spotAsked || 0;
    return calculateProfitTierSplit(ctx.profit_tiers, totalVolume, filledVolume);
  }, [ctx.profit_tiers, pnl.balance.spotAsked, pnl.balance.spotRemaining]);

  // Calculate budget and spent for BuyOpen state
  const buyOpenProgress = useMemo(() => {
    if (strategy.current_state !== 'BuyOpen') return null;

    const budget = (opportunity as any)?.max_profitable_volume || 0;

    // Calculate total spent from all spot orders (converted to USDT)
    // Use order.total if available, otherwise calculate from executed_volume * price
    const spotOrders = Object.values(ctx.orders || {});
    const spotSpent = spotOrders.reduce((sum, order) => {
      const rate = order.quote_to_usdt_rate ?? 1;
      const orderTotal = order.total ?? (order.executed_volume * order.price);
      return sum + (orderTotal * rate);
    }, 0);

    // Calculate futures filled value (USDT) from futures orders
    // executed_volume * avg_price = notional value in USDT
    const futuresOrders = Object.values(ctx.futures_orders || {});
    const futuresSpent = futuresOrders.reduce((sum, order) => {
      const executedVolume = order.executed_volume ?? 0;
      const avgPrice = order.avg_price ?? order.price ?? 0;
      return sum + (executedVolume * avgPrice);
    }, 0);

    const spotPercent = budget > 0 ? (spotSpent / budget) * 100 : 0;
    const futuresPercent = budget > 0 ? (futuresSpent / budget) * 100 : 0;

    return { budget, spotSpent, futuresSpent, spotPercent, futuresPercent };
  }, [strategy.current_state, opportunity, ctx.orders, ctx.futures_orders]);

  // Per-strategy funding timer based on this strategy's next_funding_time
  const fundingTimer = useFundingTimer(strategy.realtime_prices.next_funding_time);

  // Elapsed time since opportunity detected (detected_at → now)
  const elapsedTime = useElapsedTime((opportunity as any)?.detected_at);

  // Calculate funding interval hours from previous_funding_time if available
  const fundingIntervalHours = useMemo(() => {
    const nextFunding = strategy.realtime_prices.next_funding_time;
    const prevFunding = strategy.realtime_prices.previous_funding_time;
    if (nextFunding && prevFunding && prevFunding > 0) {
      return (nextFunding - prevFunding) / 3600000;
    }
    return null;
  }, [strategy.realtime_prices.next_funding_time, strategy.realtime_prices.previous_funding_time]);

  // Symbol from opportunity
  const symbol = opportunity?.base || position?.symbol?.replace('USDT', '') || '-';

  // Spot exchange for logo display
  // Priority: 1) from spot orders, 2) buy_exchange (for BuyOpen/StandbyHedged), 3) sell_exchange (legacy)
  const spotOrders = Object.values(ctx.orders);
  const spotExchangeFromOrder = spotOrders.length > 0 ? spotOrders[0].exchange : null;

  // Get exchange index for logo and KRW conversion
  // Strategy API sends buy_exchange as string (e.g., "upbit")
  let spotExchangeIndex: number | undefined;

  // 1. Try from spot orders
  if (spotExchangeFromOrder) {
    spotExchangeIndex = getExchangeIndex(spotExchangeFromOrder);
  }

  // 2. Try from opportunity.buy_exchange (comes as string like "upbit")
  if (spotExchangeIndex === undefined && opportunity?.buy_exchange) {
    spotExchangeIndex = getExchangeIndex(String(opportunity.buy_exchange));
  }

  // 3. Fallback to sell_exchange
  if (spotExchangeIndex === undefined && opportunity?.sell_exchange) {
    spotExchangeIndex = getExchangeIndex(String(opportunity.sell_exchange));
  }

  // Get KRW/USDT conversion rate for BuyOpen state with KRW quote
  const priceData = usePriceUpdates();

  // Calculate USDT/KRW rate using BTC cross-rate (upbitBtcKrw / binanceBtcUsdt)
  const usdtKrwRate = getUsdtKrwRate(priceData);

  // Funding rate from real-time price info (convert from decimal to percentage for display)
  const fundingRate = strategy.realtime_prices.funding_rate !== null
    ? strategy.realtime_prices.funding_rate * 100
    : null;

  // Row index for alternating background
  const rowIndex = strategy.strategy_id.charCodeAt(0) % 2;
  const bgColor = rowIndex === 0 ? theme.bg.secondary : theme.bg.primary;

  // Helper for profit/loss color
  const getPnlColor = (value: number | null | undefined) => {
    if (value === null || value === undefined) return theme.text.secondary;
    return value >= 0 ? theme.profit : theme.loss;
  };

  const safeNum = (v: number | null | undefined): number => v != null && !isNaN(v) ? v : 0;


  // Total Funding Fee from metrics
  const totalFundingFees = strategy.execution_context?.metrics?.total_funding_fees ?? 0;

  const balanceTooltip = (
    <BalanceBreakdownTooltip
      pnl={pnl}
      totalFundingFees={totalFundingFees}
      theme={theme}
      formatUsdt={formatUsdt}
      formatProfit={formatProfit}
      symbol={symbol}
    />
  );

  const filledVolumeTooltip = (
    <FilledVolumeTooltip
      pnl={pnl}
      theme={theme}
      formatUsdt={formatUsdt}
      formatProfit={formatProfit}
      symbol={symbol}
    />
  );

  // Spot Current Value (USDT) use PnL hook values
  const spotCurrentValue = (pnl.spot.currentPriceUsdt ?? 0) * safeNum(pnl.balance.spotRemaining);

  // Define variables for ROI calculation that were previously in tooltip logic
  const spotCostBasis = (pnl.spot.avgPriceUsdt ?? 0) * safeNum(pnl.balance.spotRemaining);
  const netProfit = pnl.profit.netProfit ?? 0;

  // Fees from PnL hooks if available or iterate
  // Since PnL hook doesn't export fees separately yet, we iterate orders for fees
  // Spot Fees (USDT)
  const spotBuyOrders = spotOrders.filter(o => o.side === 'Bid');
  const spotFees = spotBuyOrders.reduce((sum, o) => {
    const rate = o.quote_to_usdt_rate ?? 1;
    return sum + ((o.paid_fee ?? 0) * rate);
  }, 0);

  // Futures Fees (USDT)
  const futuresOrders = Object.values(ctx.futures_orders || {});
  const futuresFees = futuresOrders.reduce((sum, o) => sum + (o.commission ?? 0), 0);

  // Profit Percentage (ROI)
  // Base is typically the initial cost (spot cost + initial futures margin/cost). 
  // For simplicity, we use Spot Cost + Futures Fees + Funding Paid as roughly the "Capital Risked" or just Spot Cost.
  // Let's use Spot Cost Basis as the primary denominator for ROI in this tooltip context.
  const roi = spotCostBasis > 0 ? (netProfit / spotCostBasis) * 100 : 0;

  const profitTooltip = (
    <Box sx={{ p: 1.5, minWidth: 250 }}>
      {/* Header */}
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'common.white' }}>
        PnL Analysis
      </Typography>
      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Net Profit Summary */}
      <Box sx={{ mb: 1.5, bgcolor: 'rgba(255,255,255,0.05)', p: 1, borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'common.white' }}>Net Profit:</Typography>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: netProfit >= 0 ? theme.profit : theme.loss, fontFamily: 'monospace' }}>
            {formatProfit(netProfit)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>ROI:</Typography>
          <Typography variant="caption" sx={{ color: roi >= 0 ? theme.profit : theme.loss, fontFamily: 'monospace' }}>
            {formatPercent(roi)}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Spot Details */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: theme.price.spot, display: 'block', mb: 0.5 }}>
          Spot Side
        </Typography>
        <Box sx={{ pl: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Current Val:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formatUsdt(spotCurrentValue)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Cost Basis:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formatUsdt(spotCostBasis)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'rgba(255,255,255,0.9)' }}>Spot PnL:</Typography>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: (pnl.profit.spotProfit ?? 0) >= 0 ? theme.profit : theme.loss, fontFamily: 'monospace' }}>
              {formatProfit(pnl.profit.spotProfit ?? 0)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Futures Details */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: theme.price.futures, display: 'block', mb: 0.5 }}>
          Futures Side
        </Typography>
        <Box sx={{ pl: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>PnL:</Typography>
            <Typography variant="caption" sx={{ color: (pnl.profit.futuresProfit ?? 0) >= 0 ? theme.profit : theme.loss, fontFamily: 'monospace' }}>
              {formatProfit(pnl.profit.futuresProfit ?? 0)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Funding:</Typography>
            <Typography variant="caption" sx={{ color: totalFundingFees >= 0 ? theme.profit : theme.loss, fontFamily: 'monospace' }}>
              {totalFundingFees >= 0 ? '+' : '-'}${Math.abs(totalFundingFees).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Fee Breakdown */}
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
          Fees Paid
        </Typography>
        <Box sx={{ pl: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Spot:</Typography>
            <Typography variant="caption" sx={{ color: theme.loss, fontFamily: 'monospace' }}>-{formatUsdt(spotFees).replace('$', '')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Futures:</Typography>
            <Typography variant="caption" sx={{ color: theme.loss, fontFamily: 'monospace' }}>-{formatUsdt(futuresFees).replace('$', '')}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // Hover handlers
  const handleMouseEnter = useCallback(() => {
    onHoverStart?.(strategy.strategy_id);
  }, [onHoverStart, strategy.strategy_id]);

  const handleMouseLeave = useCallback(() => {
    onHoverEnd?.();
  }, [onHoverEnd]);

  // Click anywhere on the row to toggle selection
  const handleRowClick = useCallback((event: React.MouseEvent) => {
    // Don't toggle selection if clicking on interactive elements (buttons, links, etc.)
    const target = event.target as HTMLElement;
    const isInteractive = target.closest('button, a, [role="button"], .MuiIconButton-root, .MuiMenu-root');
    if (isInteractive) return;

    onSelectionChange?.(strategy.strategy_id, !isSelected);
  }, [strategy.strategy_id, isSelected, onSelectionChange]);

  return (
    <Box
      onClick={handleRowClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        display: 'flex',
        backgroundColor: isSelected
          ? 'rgba(88, 166, 255, 0.2)'
          : isHovered
            ? 'rgba(88, 166, 255, 0.15)'
            : bgColor,
        py: 1,
        px: 1,
        borderBottom: `1px solid ${theme.border}`,
        transition: 'background-color 0.15s ease',
        position: 'relative',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: isSelected ? 'rgba(88, 166, 255, 0.25)' : theme.bg.hover,
        },
      }}
    >
      {/* Selection Checkbox - absolute positioned like lock icon, doesn't take space */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            left: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Checkbox
            checked={true}
            size="small"
            sx={{
              color: theme.accent,
              '&.Mui-checked': {
                color: theme.accent,
              },
              padding: 0,
              '& .MuiSvgIcon-root': {
                fontSize: 18,
              },
            }}
          />
        </Box>
      )}
      {/* Lock indicator when row is frozen */}
      {isHovered && (
        <Box
          sx={{
            position: 'absolute',
            left: -4,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <LockIcon sx={{ fontSize: 14, color: theme.accent }} />
        </Box>
      )}
      {/* Symbol with Exchange Logo - 12% */}
      <Box
        onClick={onSymbolClick ? () => onSymbolClick(symbol) : undefined}
        sx={{
          width: '12%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          cursor: onSymbolClick ? 'pointer' : 'default',
          '&:hover': onSymbolClick ? {
            '& .symbol-text': {
              color: theme.accent,
            },
          } : {},
        }}
      >
        {spotExchangeIndex !== undefined && (
          <Avatar
            src={getExchangeLogo(spotExchangeIndex)}
            sx={{ width: 20, height: 20, flexShrink: 0 }}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
          <Typography
            className="symbol-text"
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '0.9rem', md: '1rem' },
              color: theme.text.primary,
              fontFamily: 'monospace',
              transition: 'color 0.15s ease',
              textAlign: 'left',
            }}
          >
            {symbol}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.7rem', md: '0.8rem' },
              color: strategy.is_monitoring_paused ? theme.warning : getStateColor(strategy.current_state),
              fontWeight: 500,
              mt: 0.25,
              textAlign: 'left',
            }}
          >
            {strategy.is_monitoring_paused ? '일시정지' : getStateDisplayNameKo(strategy.current_state)}
            {elapsedTime && <Box component="span" sx={{ color: theme.text.secondary, ml: 0.5, fontSize: { xs: '0.65rem', md: '0.7rem' } }}>{elapsedTime}</Box>}
          </Typography>
          {/* For Standby state: show budget and entry diff on one line below state */}
          {strategy.current_state === 'Standby' && opportunity && (opportunity as any).max_profitable_volume && (
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.65rem', md: '0.7rem' },
                mt: 0.25,
                textAlign: 'left',
                fontFamily: 'monospace',
              }}
            >
              <Box component="span" sx={{ color: theme.text.secondary }}>
                ${(opportunity as any).max_profitable_volume.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </Box>
              {' '}
              <Box component="span" sx={{ color: ((opportunity as any).quote_conversion_rate || 0) < 0 ? theme.profit : theme.loss }}>
                {((opportunity as any).quote_conversion_rate * 100)?.toFixed(2) || '0.00'}%
              </Box>
            </Typography>
          )}
        </Box>
      </Box>

      {/* Purchased Price (Spot / Futures) - 12% */}
      {/* For BuyOpen: show Budget and Entry Diff instead */}
      <Box sx={{ width: '12%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
        {strategy.current_state === 'BuyOpen' && opportunity ? (
          <>
            {/* Budget (max_profitable_volume) */}
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: theme.text.primary, fontFamily: 'monospace' }}>
              ${(opportunity as any).max_profitable_volume?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '-'}
            </Typography>
            {/* Entry Diff (quote_conversion_rate as percentage) */}
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.75rem', md: '0.85rem' },
                color: ((opportunity as any).quote_conversion_rate || 0) < 0 ? theme.profit : theme.loss,
                fontFamily: 'monospace',
                mt: 0.5,
              }}
            >
              {((opportunity as any).quote_conversion_rate * 100)?.toFixed(2) || '0.00'}%
            </Typography>
            {/* BuyOpen Mode (지정가/시장가) */}
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.65rem', md: '0.75rem' },
                color: strategy.execution_context.buyopen_mode?.type === 'Market' ? '#ff9800' : theme.text.secondary,
                mt: 0.25,
              }}
            >
              {strategy.execution_context.buyopen_mode?.type === 'Market'
                ? `시장가 $${(strategy.execution_context.buyopen_mode as any).max_notional_usdt || 0}`
                : '지정가'}
            </Typography>
          </>
        ) : (
          <>
            {/* Spot price - show original quote currency first if non-USDT */}
            {pnl.spot.isNonUsdtQuote && pnl.spot.avgPriceQuote ? (
              <>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' }, color: theme.text.secondary }}>
                  {pnl.spot.quoteCurrency === 'KRW' ? '₩' : pnl.spot.quoteCurrency === 'BTC' ? '₿' : ''}
                  {pnl.spot.avgPriceQuote.toLocaleString('en-US', { maximumFractionDigits: pnl.spot.quoteCurrency === 'KRW' ? 2 : 8 })}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: theme.price.spot, fontFamily: 'monospace' }}>
                  {formatUsdt(pnl.spot.avgPriceUsdt)}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: theme.price.spot, fontFamily: 'monospace' }}>
                {formatUsdt(pnl.spot.avgPriceUsdt)}
              </Typography>
            )}
            {/* Futures price - always in USDT */}
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: theme.price.futures, fontFamily: 'monospace', mt: 0.5 }}>
              {formatUsdt(pnl.futures.avgPrice)}
            </Typography>
          </>
        )}
      </Box>

      {/* Current Price (Spot / Futures) - 12% */}
      {/* For BuyOpen: show realtime market prices with quote from opportunity */}
      {/* Uses spot_best_ask (what we pay to buy) for BuyOpen entry calculation */}
      <Box sx={{ width: '12%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
        {strategy.current_state === 'BuyOpen' && opportunity ? (
          (() => {
            const buyQuote = (opportunity as any).buy_quote;
            const isKrwQuote = buyQuote === 'K' || buyQuote === 'KRW';
            // For BuyOpen: use spot_best_ask (what we pay to buy)
            const spotAsk = strategy.realtime_prices.spot_best_ask;

            // Convert KRW to USDT if needed (using rate with fallback)
            const spotAskUsdt = isKrwQuote && spotAsk && usdtKrwRate
              ? spotAsk / usdtKrwRate
              : spotAsk;

            return (
              <>
                {/* Spot price - show in buy_quote currency if KRW */}
                {isKrwQuote ? (
                  <>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' }, color: theme.text.secondary }}>
                      ₩{spotAsk?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: theme.price.spot, fontFamily: 'monospace' }}>
                      {formatUsdt(spotAskUsdt)}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: theme.price.spot, fontFamily: 'monospace' }}>
                    {formatUsdt(spotAsk)}
                  </Typography>
                )}
                {/* Futures mark price - always USDT */}
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: theme.price.futures, fontFamily: 'monospace', mt: 0.5 }}>
                  {formatUsdt(strategy.realtime_prices.futures_mark_price)}
                </Typography>
              </>
            );
          })()
        ) : (
          <>
            {/* Spot current price - show original quote currency first if non-USDT */}
            {pnl.spot.isNonUsdtQuote && pnl.spot.currentPriceQuote ? (
              <>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' }, color: theme.text.secondary }}>
                  {pnl.spot.quoteCurrency === 'KRW' ? '₩' : pnl.spot.quoteCurrency === 'BTC' ? '₿' : ''}
                  {pnl.spot.currentPriceQuote.toLocaleString('en-US', { maximumFractionDigits: pnl.spot.quoteCurrency === 'KRW' ? 2 : 8 })}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: theme.price.spot, fontFamily: 'monospace' }}>
                  {formatUsdt(pnl.spot.currentPriceUsdt)}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: theme.price.spot, fontFamily: 'monospace' }}>
                {formatUsdt(pnl.spot.currentPriceUsdt)}
              </Typography>
            )}
            {/* Futures price - always in USDT */}
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: theme.price.futures, fontFamily: 'monospace', mt: 0.5 }}>
              {formatUsdt(pnl.futures.currentPrice)}
            </Typography>
          </>
        )}
      </Box>

      {/* Liquidation Price - 10% */}
      {/* For BuyOpen: show current difference rate (spot_ask vs futures_bid) */}
      <Box sx={{ width: '10%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {strategy.current_state === 'BuyOpen' && opportunity ? (
          (() => {
            const buyQuote = (opportunity as any).buy_quote;
            const isKrwQuote = buyQuote === 'K' || buyQuote === 'KRW';
            // For BuyOpen: use spot_best_ask (what we pay to buy)
            const spotAsk = strategy.realtime_prices.spot_best_ask;
            const futuresBid = strategy.realtime_prices.futures_best_bid;

            // Convert spot to USDT if KRW (using rate with fallback)
            const spotAskUsdt = isKrwQuote && spotAsk && usdtKrwRate
              ? spotAsk / usdtKrwRate
              : spotAsk;

            // Calculate difference: (spot_ask - futures_bid) / spot_ask * 100
            // Same formula as opportunity table (FuturesDataRow)
            const diffPercent = spotAskUsdt && futuresBid
              ? ((spotAskUsdt - futuresBid) / spotAskUsdt) * 100
              : null;

            return (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                    color: diffPercent !== null
                      ? (diffPercent < 0 ? theme.profit : theme.loss)
                      : theme.text.secondary,
                    fontWeight: 600,
                    fontFamily: 'monospace',
                  }}
                >
                  {diffPercent !== null ? `${diffPercent.toFixed(2)}%` : '-'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                    color: theme.text.secondary,
                    mt: 0.25,
                  }}
                >
                  현재 차이
                </Typography>
              </>
            );
          })()
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.8rem', md: '0.9rem' },
                color: position?.liquidation_price ? theme.danger : theme.text.secondary,
                fontWeight: position?.liquidation_price ? 600 : 400,
                fontFamily: 'monospace',
              }}
            >
              {formatUsdt(position?.liquidation_price)}
            </Typography>
            {/* How close to liquidation: current_price / liquidation_price */}
            {position?.liquidation_price && pnl.futures.currentPrice && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.65rem', md: '0.75rem' },
                  color: (pnl.futures.currentPrice / position.liquidation_price) > 0.9 ? theme.danger : theme.text.secondary,
                  mt: 0.25,
                  fontFamily: 'monospace',
                }}
              >
                {((pnl.futures.currentPrice / position.liquidation_price) * 100).toFixed(1)}%
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Funding Rate + Timer - 12% */}
      <Box sx={{ width: '12%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.75rem',
              color: totalFundingFees >= 0 ? theme.profit : theme.loss,
              fontFamily: 'monospace',
              lineHeight: 1.2,
              mb: 0.7,
              opacity: 0.75,
            }}
          >
            {totalFundingFees >= 0 ? '+' : '-'}${Math.abs(totalFundingFees).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: { xs: '0.8rem', md: '0.9rem' },
              color: fundingRate === null ? theme.text.secondary : (fundingRate >= 0 ? theme.profit : theme.loss),
              fontFamily: 'monospace',
              opacity: 1,
              lineHeight: 1.2,
            }}
          >
            {formatPercent(fundingRate)}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            ...subTextStyle,
            fontFamily: 'monospace',
            color: theme.text.secondary,
            textAlign: 'center',
          }}
        >
          {fundingTimer}{fundingIntervalHours !== null && ` (${fundingIntervalHours}h)`}
        </Typography>
      </Box>

      {/* Remaining Balance (Spot / Futures) - 6% */}
      <Box sx={{ width: '6%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Tooltip title={balanceTooltip} arrow placement="top">
          <Box sx={{ cursor: 'pointer' }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' }, color: theme.price.spot, fontFamily: 'monospace' }}>
              {formatPositionVolume(pnl.balance.spotRemaining)}
            </Typography>
            <Typography variant="body2" sx={{ ...subTextStyle, color: theme.price.futures, fontFamily: 'monospace' }}>
              {formatPositionVolume(pnl.balance.futuresRemaining)}
            </Typography>
            {/* For BuyOpen: Show spot and futures fill percentages */}
            {buyOpenProgress && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.6rem', md: '0.65rem' },
                  fontFamily: 'monospace',
                  mt: 0.25,
                }}
              >
                <Box component="span" sx={{ color: theme.price.spot, opacity: 0.7 }}>
                  {buyOpenProgress.spotPercent.toFixed(1)}%
                </Box>
                {' '}
                <Box component="span" sx={{ color: theme.price.futures, opacity: 0.7 }}>
                  {buyOpenProgress.futuresPercent.toFixed(1)}%
                </Box>
              </Typography>
            )}
            {/* For other states: Show remaining profit tiers on one line below balances if configured */}
            {!buyOpenProgress && profitTierSplit.remaining.length > 0 && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.6rem', md: '0.65rem' },
                  color: theme.text.secondary,
                  fontFamily: 'monospace',
                  mt: 0.25,
                }}
              >
                {profitTierSplit.remaining.map((tier, idx) =>
                  `${idx > 0 ? ' ' : ''}${(tier.profit_percentage * 100).toFixed(1)}%→${(tier.volume_percentage_to_sell * 100).toFixed(0)}%`
                ).join('')}
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>

      {/* Traded Volume (Spot Asked / Futures Closed) - 6% */}
      <Box sx={{ width: '6%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Tooltip title={filledVolumeTooltip} arrow placement="top">
          <Box sx={{ cursor: 'pointer' }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' }, color: theme.price.spot, fontFamily: 'monospace' }}>
              {formatPositionVolume(pnl.balance.spotAsked)}
            </Typography>
            <Typography variant="body2" sx={{ ...subTextStyle, color: theme.price.futures, fontFamily: 'monospace' }}>
              {formatPositionVolume(pnl.balance.futuresClosed)}
            </Typography>
            {/* Show filled profit tiers on one line below volumes if configured */}
            {profitTierSplit.filled.length > 0 && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.6rem', md: '0.65rem' },
                  color: theme.text.secondary,
                  fontFamily: 'monospace',
                  mt: 0.25,
                }}
              >
                {profitTierSplit.filled.map((tier, idx) =>
                  `${idx > 0 ? ' ' : ''}${(tier.profit_percentage * 100).toFixed(1)}%→${(tier.volume_percentage_to_sell * 100).toFixed(0)}%`
                ).join('')}
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>

      {/* Profit (Net / Spot / Futures) - 20% */}
      <Box sx={{ width: '20%', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'right', pr: 4 }}>
        <Tooltip title={profitTooltip} arrow placement="top">
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.9rem', md: '1rem' },
              fontWeight: 600,
              color: getPnlColor(pnl.profit.netProfit),
              fontFamily: 'monospace',
              cursor: 'pointer',
              width: 'fit-content',
              alignSelf: 'flex-end',
            }}
          >
            {formatProfit(pnl.profit.netProfit)}
          </Typography>
        </Tooltip>
        <Typography
          variant="body2"
          sx={{
            ...subTextStyle,
            fontFamily: 'monospace',
            color: theme.price.spot,
          }}
        >
          {formatProfit(pnl.profit.spotProfit)}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            ...subTextStyle,
            fontFamily: 'monospace',
            color: theme.price.futures,
          }}
        >
          {formatProfit(pnl.profit.futuresProfit)}
        </Typography>
      </Box>

      {/* Action Menu - positioned at far right */}
      <Box sx={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)' }}>
        <StrategyActionsMenu
          strategyId={strategy.strategy_id}
          currentState={strategy.current_state}
          isMonitoringPaused={strategy.is_monitoring_paused}
          supportsActions={!['Success', 'Failure', 'Abort'].includes(strategy.current_state)}
          loadingAction={loadingAction}
          onAction={onAction}
        />
      </Box>
    </Box>
  );
});

PositionRow.displayName = 'PositionRow';

export default PositionRow;
